import { datadogRum } from '@datadog/browser-rum';
import { authExchange } from '@urql/exchange-auth';
import { cacheExchange } from '@urql/exchange-graphcache';
import { relayPagination } from '@urql/exchange-graphcache/extras';
import schema from 'gql-schema.min.json';
import { createClient, errorExchange, fetchExchange } from 'urql';

import { deleteCookie, getCookie } from 'lib/cookies';
import { globalVariables } from 'lib/globalVariables';
import { graphqlErrorHandler } from 'lib/graphqlErrorHandler';
import { isImpersonating } from 'utils/impersonation';

import { activeQueriesExchange } from './activeQueries';
import { AUTH_CONFIG } from './auth';
import {
  TRIAL_COOKIE_REVOKED_EVENT,
  TRIAL_SESSION_COOKIE,
  isAuthExpiredError,
  resolveAuthHeaders,
  shouldAuthError,
} from './authHeaders';
import { keys } from './cache/keys';
import { mutationUpdates } from './cache/mutationUpdates';
import { optimistic } from './cache/optimistic';
import { subscriptionUpdates } from './cache/subscriptionUpdates';
import { serialiseMutationsExchange } from './serialiseMutationsExchange';
import { createSubscriptionClient } from './subscriptionExchange';

function createUrqlClient() {
  return createClient({
    exchanges: [
      activeQueriesExchange,
      // cacheExchange ahead of authExchange (URQL's recommended order) so cache
      // hits return without queuing behind a token refresh. Built per-client so
      // resetUrqlClient() (team switch / logout) starts from a fresh cache.
      cacheExchange({
        keys,
        schema,
        updates: { Mutation: mutationUpdates, Subscription: subscriptionUpdates },
        resolvers: { Query: { bookingTaskResults: relayPagination() } },
        optimistic,
      }),
      authExchange(async utils => {
        let token = await AUTH_CONFIG.getToken();

        return {
          didAuthError: value => value.graphQLErrors.some(isAuthExpiredError),
          willAuthError: () => shouldAuthError(token, getCookie(TRIAL_SESSION_COOKIE)),
          refreshAuth: async () => {
            token = await AUTH_CONFIG.getToken();
          },
          addAuthToOperation: operation => {
            const headers = resolveAuthHeaders(token, getCookie(TRIAL_SESSION_COOKIE));
            if (!headers) return operation;
            return utils.appendHeaders(operation, headers);
          },
        };
      }),
      errorExchange({
        onError: (error, operation) => {
          // Subscriptions bypass authExchange and components rarely read the
          // useSubscription `error` field, so GraphQL-level subscription errors
          // would otherwise vanish. Connection-level failures are covered by the
          // graphql-ws `on` handlers, so only log graphQLErrors here.
          if (operation.kind === 'subscription' && error.graphQLErrors.length > 0) {
            datadogRum.addError(error, { source: 'graphql-subscription' });
          }

          // AuthWrapper clears the trial cookie on Kinde login, so a 401 with that
          // cookie is a stale trial session. Clear it so StreamsTrialGate re-mounts
          // TrialInitializer, which mints a fresh cookie or shows an error UI.
          const has401 = error.graphQLErrors.some(isAuthExpiredError);
          if (has401 && getCookie(TRIAL_SESSION_COOKIE)) {
            deleteCookie(TRIAL_SESSION_COOKIE);
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event(TRIAL_COOKIE_REVOKED_EVENT));
            }
          }

          graphqlErrorHandler({
            graphqlErrors: error.graphQLErrors,
            networkError: error.networkError,
          });
        },
      }),
      // After cacheExchange, so optimistic updates still apply as soon as a mutation is queued.
      serialiseMutationsExchange,
      fetchExchange,
      createSubscriptionClient(),
    ],
    url: globalVariables.getEnvironmentVariables().graphqlURI,
    fetchOptions: () => {
      return {
        credentials: 'include',
        headers: {
          'x-graphql-client-name': 'clients',
          'x-graphql-client-version': import.meta.env.VITE_APP_VERSION ?? 'N/A',
          ...(isImpersonating() ? { 'x-askable-admin': 'true' } : {}),
        },
      };
    },
  });
}

// Swapped out on a team switch, so callers read it through the getter rather than
// holding the instance.
let urqlClient = createUrqlClient();

export function getUrqlClient() {
  return urqlClient;
}

export function resetUrqlClient() {
  urqlClient = createUrqlClient();
  return urqlClient;
}
